/* eslint-disable no-unused-vars */
import { HTMLAttributes, useEffect, useState } from 'react'
import cn from 'classnames'
import Image from 'next/legacy/image'
import OutsideClickHandler from 'react-outside-click-handler'
// Components
import styles from './Selector.module.scss'

interface ISelect extends HTMLAttributes<HTMLDivElement> {
  options?: { value: string; label: string; image?: string }[]
  value:
    | { value: string; label: string; image?: string }
    | { value: string; label: string; image?: string }[]
  placeholder?: string
  onChange: (value: any) => void
  disabled?: boolean
  theme?: string | string[]
  showError?: boolean
  errorMessage?: string
  hasImage?: boolean
  // Filterable
  isFilterable?: boolean
  // Multiple
  isMultiple?: boolean
  // Theme admin
  isAdmin?: boolean
}

export default function Select({
  options: _options = [],
  value,
  placeholder,
  onChange,
  disabled,
  theme,
  showError,
  errorMessage,
  hasImage,
  // Filterable
  isFilterable,
  // Multiple
  isMultiple: _isMultiple,
  // Theme admin
  isAdmin,
}: ISelect) {
  // Handles themes
  const classes = { [styles.option]: true }
  if (typeof theme === 'string') theme = [theme]
  theme?.forEach((t) => {
    classes[styles[t]] = typeof t !== 'undefined'
  })

  const isMultiple = _isMultiple && Array.isArray(value) // Multiple
  const [showOptions, setShowOptions] = useState(false) // Creatable
  const [filter, setFilter] = useState(
    Array.isArray(value) ? '' : value?.label || ''
  ) // Filterable

  useEffect(() => {
    if (!Array.isArray(value) && value?.label) setFilter(value?.label)
  }, [value])

  const options = isFilterable
    ? _options?.filter((o) =>
        o.label?.toLowerCase().includes(filter.toLowerCase())
      )
    : _options

  return (
    <div
      className={cn({
        [styles.container]: true,
      })}
    >
      <OutsideClickHandler
        onOutsideClick={() => {
          if (showOptions) {
            setShowOptions(false)
            if (isFilterable && !Array.isArray(value) && value?.label)
              setFilter(value?.label)
          }
        }}
      >
        {Array.isArray(value) && value?.length > 0 && (
          <div
            className={cn({
              [styles.multiple]: true,
              [styles.input]: true,
              [styles.showError]: showError,
            })}
          >
            <div
              className={cn({
                [styles.scroll]: !isAdmin,
                [styles.square]: isAdmin,
              })}
            >
              {value.map((o, i) => (
                <div
                  key={i}
                  className={cn({
                    [styles.selected]: !isAdmin,
                    [styles.black]: isAdmin,
                  })}
                >
                  <div className={styles.label}>{o.label}</div>
                  <div
                    className={styles.clear}
                    onClick={() =>
                      onChange(
                        value.filter((selected) => selected.value !== o.value)
                      )
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <input
          className={cn({
            [styles.margin]:
              isMultiple && (!Array.isArray(value) || !value.length),
            [styles.showError]: showError,
          })}
          type="text"
          placeholder={placeholder}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onClick={() => {
            setShowOptions(true)
          }}
        />
        <br />
        {/* Modal */}
        {showOptions && (
          <div className={styles.optionsContainer}>
            <div className={styles.arrow} />
            <div
              className={cn({
                [styles.options]: true,
              })}
            >
              {options.length ? (
                options.map((o, i) => (
                  <div
                    key={i}
                    className={cn({
                      ...classes,
                      [styles.current]:
                        isMultiple && Array.isArray(value)
                          ? value
                              .map((selected) => selected.value)
                              .includes(o.value)
                          : !Array.isArray(value) && o?.value === value?.value,
                      [styles.disabled]: disabled,
                    })}
                    onClick={() => {
                      if (isMultiple && Array.isArray(value)) {
                        if (
                          value
                            .map((selected) => selected.value)
                            .includes(o.value)
                        ) {
                          setShowOptions(false)
                        } else {
                          if (!disabled) onChange([...value, o])
                          setShowOptions(false)
                        }
                        if (isFilterable) setFilter('')
                      }
                    }}
                  >
                    {/* Option has image */}
                    {hasImage && (
                      <div className={styles.image}>
                        {o.image ? (
                          <Image
                            src={`${process.env.NEXT_PUBLIC_MEDIA_BUCKET}${o.image}`}
                            layout="fill"
                            objectFit="cover"
                            loading="lazy"
                            alt={o.label}
                          />
                        ) : null}
                      </div>
                    )}
                    <div className={styles.label}>{o.label}</div>
                  </div>
                ))
              ) : (
                <div className={styles.empty}>
                  <div className={styles.icon} />

                  <div className={styles.message}>No options found</div>
                </div>
              )}
            </div>
          </div>
        )}

        {showError && !!errorMessage && (
          <div className={styles.error}>
            <div className={styles.icon} />
            <div className={styles.message}>{errorMessage}</div>
          </div>
        )}
      </OutsideClickHandler>
    </div>
  )
}
